import { mdiAccountGroupOutline } from '@mdi/js';
import i18n from '@/i18n';
import { alertsAccessGuard } from '@/router/guards/alertsAccessGuard';
import { getAuthStore, onlyIf } from './helpers';

export default [
  {
    meta: {
      cauldron: 'platform',
      title: i18n.global.t('page_titles.user_index'),
      icon: mdiAccountGroupOutline,
      requiresAuth: true,
      hideNewsletter: true,
      hideFooter: true,
      favoritable: false,
      breadcrumbs: [
        { label: i18n.global.t('cauldron.platform.label'), to: '/profile' },
        { label: i18n.global.t('page_titles.users'), to: '/users' },
      ],
    },
    path: '/users',
    name: 'users.index',
    ...onlyIf(
      () =>
        getAuthStore().permissions.includes('view_all_users') ||
        (getAuthStore().tenantType === 'company' &&
          getAuthStore().permissions.includes('view_users'))
    ),
    component: () => import('@/pages/account/user/UserIndex.vue'),
  },
  // `/alerts` used to be a standalone index page (`AlertIndex.vue`), but
  // the alerts UI lives entirely inside the Personalisation cauldron now
  // via `/my-environment/alerts`. The redirect keeps deep links + bookmarks
  // working, and gives the create/edit forms below a single canonical
  // landing to return to after save.
  //
  // The create / build / edit pages need the `manage_own_alerts` permission AND
  // the `tool.alerts.use` entitlement (see guards/alertsAccessGuard.js).
  { path: '/alerts', redirect: '/my-environment/alerts' },
  {
    meta: {
      cauldron: 'personalisation',
      title: i18n.global.t('page_titles.alert_create'),
      breadcrumbs: [
        { label: i18n.global.t('menu_alerts'), to: '/my-environment/alerts' },
        { label: i18n.global.t('page_titles.alert_create'), to: '/alerts/create' },
      ],
    },
    path: '/alerts/create',
    name: 'alerts.create',
    beforeEnter: alertsAccessGuard,
    component: () => import('@/pages/alerts/AlertForm.vue'),
  },
  // Context-driven builder: launched from a dataset/series with the target
  // pre-resolved via query params (?datasetId=&seriesId=&filters=&strip=), so
  // it opens straight on condition + channels with a live preview — no dataset
  // browsing. Runs side-by-side with the /alerts/create wizard for now.
  {
    meta: {
      cauldron: 'personalisation',
      title: i18n.global.t('page_titles.alert_create'),
      breadcrumbs: [
        { label: i18n.global.t('menu_alerts'), to: '/my-environment/alerts' },
        { label: i18n.global.t('page_titles.alert_create'), to: '/alerts/build' },
      ],
    },
    path: '/alerts/build',
    name: 'alerts.build',
    beforeEnter: alertsAccessGuard,
    component: () => import('@/pages/alerts/AlertBuilder.vue'),
  },
  {
    meta: {
      cauldron: 'personalisation',
      title: i18n.global.t('page_titles.alert_edit'),
      breadcrumbs: [
        { label: i18n.global.t('menu_alerts'), to: '/my-environment/alerts' },
        { label: i18n.global.t('page_titles.alert_edit'), to: '#' },
      ],
    },
    path: '/alerts/:id/edit',
    name: 'alerts.edit',
    beforeEnter: alertsAccessGuard,
    component: () => import('@/pages/alerts/AlertForm.vue'),
  },
  {
    meta: {
      cauldron: 'platform',
      title: i18n.global.t('page_titles.user_create'),
      icon: mdiAccountGroupOutline,
      requiresAuth: true,
      hideNewsletter: true,
      hideFooter: true,
      favoritable: false,
      breadcrumbs: [
        { label: i18n.global.t('cauldron.platform.label'), to: '/profile' },
        { label: i18n.global.t('page_titles.users'), to: '/users' },
        { label: i18n.global.t('page_titles.user_create'), to: '/users/create' },
      ],
    },
    path: '/users/create',
    name: 'users.create',
    ...onlyIf(() => getAuthStore().permissions.includes('create_users')),
    component: () => import('@/pages/account/user/UserCreate.vue'),
  },
  {
    meta: {
      cauldron: 'platform',
      title: i18n.global.t('page_titles.edit_user'),
      icon: mdiAccountGroupOutline,
      requiresAuth: true,
      hideNewsletter: true,
      hideFooter: true,
      favoritable: false,
      breadcrumbs: [
        { label: i18n.global.t('cauldron.platform.label'), to: '/profile' },
        { label: i18n.global.t('page_titles.users'), to: '/users' },
        { label: i18n.global.t('page_titles.edit_user'), to: '#' },
      ],
    },
    path: '/user/:slug/edit',
    name: 'users.edit',
    ...onlyIf(() => getAuthStore().permissions.includes('update_users')),
    component: () => import('@/pages/account/user/UserEdit.vue'),
  },
  {
    meta: {
      title: i18n.global.t('page_titles.request_company_account'),
    },
    path: '/company',
    name: 'company.form',
    component: () => import('@/pages/account/company/CompanyForm.vue'),
  },
  {
    meta: {
      title: i18n.global.t('page_titles.manage_company'),
    },
    path: '/company/manage',
    name: 'company.manage',
    component: () => import('@/pages/account/company/ManageCompany.vue'),
  },
  {
    meta: {
      title: i18n.global.t('menu_subscriptions'),
      formScreen: true,
      requiresAuth: true,
    },
    path: '/stripe/subscribed-callback',
    name: 'stripe.subscribed-callback',
    component: () => import('@/pages/account/billing/SubscribedCallback.vue'),
  },
  {
    meta: {
      title: i18n.global.t('page_titles.verify_phone'),
    },
    path: '/verify-phone',
    name: 'phone.verify',
    component: () => import('@/pages/verify-phone/VerifyPhone.vue'),
  },
];
